import { Component, Input, OnInit } from '@angular/core';
import { CrudService } from 'src/app/services/crud.service';
import { environment } from 'src/environments/environment';
import { LoadingService } from '../../report-loading.service';
import { DxSelectBoxModule, DxButtonModule, DxValidatorModule, DxValidationSummaryModule } from 'devextreme-angular';
import validationEngine from 'devextreme/ui/validation_engine';
import notify from 'devextreme/ui/notify';

/**
 * Thirsty Camel Core Range POS brief — download only.
 *
 * Pick a core range by name and download the POS brief spreadsheet for its auditable
 * products. No buying group step: the export spans every TC store size header of the
 * chosen range and reports the sizes a product is ranged in via the TYPE column, so
 * narrowing to one buying group up front would only hide rows.
 *
 * Deliberately does NOT use the shared app-filter component — that filter is built around
 * a promotion period plus a buying group, and this report needs neither.
 */
@Component({
    selector: 'app-core-range-media-plan-export-thirsty',
    templateUrl: './core-range-media-plan-export-thirsty.component.html',
    styleUrls: ['./core-range-media-plan-export-thirsty.component.css'],
    imports: [ DxSelectBoxModule, DxButtonModule, DxValidatorModule, DxValidationSummaryModule ],
    standalone: true
})
export class CoreRangeMediaPlanExportThirstyComponent implements OnInit {
  @Input() name: string;
  @Input() reportComponent: string;

  private apiUrl: string = environment.baseApiUrl;
  validationGroup = 'coreRangePosBriefGroup';
  rangeNames: string[] = [];
  selectedRangeName: string | null = null;
  loading: boolean = false;

  constructor(private crudService: CrudService,
              private loadingService: LoadingService) { }

  async ngOnInit(): Promise<void> {
    await this.getRangeNames();
  }

  /**
   * Core range headers come back one per buying group, newest first — collapse them to the
   * distinct range names, preserving that order.
   */
  private async getRangeNames(): Promise<void> {
    try {
      const response = await this.crudService.getData(`${this.apiUrl}core-range-headers`).toPromise();
      const headers = response?.data ?? [];
      this.rangeNames = [...new Set(headers.map((header: any) => header.range_name).filter(Boolean))] as string[];
    } catch (error) {
      console.error('Failed to fetch core range headers:', error);
      notify('Could not load core ranges', 'error', 3000);
    }
  }

  exportReport(): void {
    const result = validationEngine.validateGroup(this.validationGroup);
    if (!result.isValid) {
      return;
    }

    const rangeName = this.selectedRangeName as string;
    const now = new Date();
    const dateStr = `${String(now.getDate()).padStart(2, '0')}${String(now.getMonth() + 1).padStart(2, '0')}${now.getFullYear()}`;

    this.setLoading(true);
    this.exportToXlsx(
      `${this.apiUrl}export/core-range-media-planTC?range_name=${encodeURIComponent(rangeName)}`,
      `POS Brief Core Range ${rangeName} ${dateStr}.xlsx`
    );
  }

  private setLoading(isLoading: boolean): void {
    this.loading = isLoading;
    this.loadingService.toggleLoading(isLoading);
  }

  private exportToXlsx(endpoint: string, filename: string): void {
    this.crudService.getExportData(endpoint, {}, 'blob').subscribe(
      (response: Blob) => {
        const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const downloadLink = document.createElement('a');
        const url = window.URL.createObjectURL(blob);
        downloadLink.href = url;
        downloadLink.download = filename;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
        window.URL.revokeObjectURL(url);
        this.setLoading(false);
      },
      (error: any) => {
        console.error('Error:', error);
        notify('Export failed', 'error', 3000);
        this.setLoading(false);
      }
    );
  }
}
